import { useAppDispatch, useAppSelector } from 'hooks/redux';
import React, { useCallback, useEffect, type JSX } from 'react';
import { useDispatch, useStore } from 'react-redux';

import { categoriesSelectors } from 'app-components/flux/categories';
import { getFeatureFlag } from 'app-components/flux/feature-flags/featureFlagsSelectors';
import { clearState } from 'app-components/flux/transaction-rules/transactionRulesForm';
import { FormProvider } from 'app-components/flux/transaction-rules/transactionRulesFormHooks';
import type { SubmitHandler, SubmitPayload } from 'app-components/flux/transaction-rules/transactionRulesTypes';
import {
  handleBackNavAction,
  updateDuplicateIdAction,
  updateErrorAction,
  updateRuleAction,
  updateStageAction,
} from 'app-components/flux/transaction-rules/transactionRuleActions';
import { useField, useManager } from 'app-components/flux/transaction-rules/transactionRulesFormHooks';
import { RuleProvider, useStateContext } from 'app-components/flux/transaction-rules/transactionRulesFormState';
import { getDuplicateRuleId } from 'app-components/flux/transaction-rules/transactionRulesSelectors';
import {
  CLEARED,
  EDITING,
  RESOLVING,
  SUBMITTING,
  VALIDATED,
  type TransactionRuleType,
} from 'app-components/flux/transaction-rules/transactionRulesTypes';
import { checkRuleConditions } from 'app-components/flux/transaction-rules/transactionRulesUtils';
import type { TransactionProps } from 'app-components/flux/transactions/transactionsTypes';
import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import StdDialogContent from 'components/Dialogs/StdDialogContent';
import { cancelTransactionRuleDialog } from 'components/Dialogs/CancelTransactionRule/actions';
import { deleteTransactionRuleDialog } from 'components/Dialogs/DeleteTransactionRule/actions';
import RuleEditorPanels from 'containers/Settings/ChildPages/RulesSimplifi/RuleEditorPanels';
import RuleFooter from 'containers/Settings/ChildPages/RulesSimplifi/RuleFooter';
import RuleReview from 'containers/Settings/ChildPages/RulesSimplifi/RuleReview';
import {
  getEnabledOverrides,
  getInitialRule,
  getNormalizedPayeeNames,
} from 'components/Dialogs/RuleDialogs/shared/ruleDialogUtils';
import { submitTransactionRule } from 'components/Dialogs/RuleDialogs/shared/ruleDialogSubmit';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TurnRightIcon from '@mui/icons-material/TurnRight';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

export type LlmRuleDialogProps = {
  dialogId?: string;
  transaction?: TransactionProps;
  rule?: TransactionRuleType;
  hasAIEdits?: boolean;
  handleClose?: (rule?: TransactionRuleType) => void;
  originalIsReviewed?: boolean | null;
};

const RuleInstructions = (): JSX.Element => {
  const { value: ifPayee } = useField('isEnabledPayeeSource');
  const { value: ifAmount } = useField('isEnabledAmountSource');

  if (ifPayee) {
    return (
      <>
        <Typography variant='paragraphRegular'>Now, add THEN conditions that you want your rule to change.</Typography>
        <TurnRightIcon sx={{ transform: 'rotate(90deg) scale(1, 1)' }} />
      </>
    );
  }

  if (ifAmount) {
    return <Typography variant='paragraphRegular'>Add another IF condition to your rule</Typography>;
  }

  return (
    <>
      <TurnRightIcon sx={{ transform: 'rotate(90deg) scale(1, -1)' }} />
      <Typography variant='paragraphRegular'>Add IF conditions that will target transactions.</Typography>
    </>
  );
};

const RuleDialogContent = ({ dialogId }: { dialogId?: string }): JSX.Element => {
  const { state, dispatch } = useStateContext();
  const { stage, rule, onUpdate, backText, titleText, enabledOverrides } = state;
  const manager = useManager();
  const rootDispatch = useDispatch();

  const { onValueChange: payeeSource } = useField('isEnabledPayeeSource');
  const { onValueChange: amountSource } = useField('isEnabledAmountSource');
  const { onValueChange: payeeTarget } = useField('isEnabledPayeeTarget');
  const { onValueChange: coaTarget } = useField('isEnabledCoaTarget');
  const { onValueChange: tagsTarget } = useField('isEnabledTagsTarget');
  const { onValueChange: memoTarget } = useField('isEnabledMemoTarget');
  const { onValueChange: excludedTarget } = useField('isEnabledExcludedTarget');
  const { onValueChange: reviewedTarget } = useField('isEnabledReviewedTarget');

  // Override condition enabled flags if provided (e.g. when creating a rule from a transaction)
  // This is a web-only feature for now
  useEffect(() => {
    if (enabledOverrides) {
      Object.entries(enabledOverrides).forEach(([key, value]) => {
        if (typeof value === 'boolean') {
          eval(`${key}(${value})`); // eslint-disable-line no-eval
        }
      });
    }
  }, [
    enabledOverrides,
    manager,
    payeeSource,
    amountSource,
    payeeTarget,
    coaTarget,
    tagsTarget,
    memoTarget,
    excludedTarget,
    reviewedTarget,
  ]);

  const handleSubmit = useCallback(() => {
    if (stage === EDITING || stage === RESOLVING) {
      manager.submit(true); // runs field-level validation

      // Business rule validation: check source/target conditions
      // These checks must run regardless of field-level validation
      const hasPayeeSource = manager.getFieldValue('isEnabledPayeeSource');
      const hasAmountSource = manager.getFieldValue('isEnabledAmountSource');
      const hasAccountSource = manager.getFieldValue('isEnabledAccountSource');
      const hasPayeeTarget = manager.getFieldValue('isEnabledPayeeTarget');
      const hasCoaTarget = manager.getFieldValue('isEnabledCoaTarget');
      const hasTagsTarget = manager.getFieldValue('isEnabledTagsTarget');
      const hasMemoTarget = manager.getFieldValue('isEnabledMemoTarget');
      const hasExcludedTarget = manager.getFieldValue('isEnabledExcludedTarget');
      const hasReviewedTarget = manager.getFieldValue('isEnabledReviewedTarget');

      // Check: At least one source condition
      if (!hasPayeeSource && !hasAmountSource && !hasAccountSource) {
        dispatch(updateStageAction(EDITING));
        dispatch(updateErrorAction('Specify at least 1 IF condition'));
        return;
      }

      // Check: Amount cannot be the only source condition
      if (hasAmountSource && !hasPayeeSource && !hasAccountSource) {
        dispatch(updateStageAction(EDITING));
        dispatch(updateErrorAction('Combine Amount with another IF condition, like Payee or Account'));
        return;
      }

      // Check: Account cannot be the only source condition
      if (hasAccountSource && !hasPayeeSource && !hasAmountSource) {
        dispatch(updateStageAction(EDITING));
        dispatch(updateErrorAction('Combine Account with another IF condition, like Payee or Amount'));
        return;
      }

      // Check: At least one target condition
      if (
        !hasPayeeTarget &&
        !hasCoaTarget &&
        !hasTagsTarget &&
        !hasMemoTarget &&
        !hasExcludedTarget &&
        !hasReviewedTarget
      ) {
        dispatch(updateStageAction(EDITING));
        dispatch(updateErrorAction('Specify at least 1 THEN condition'));
        return;
      }

      // Check field-level validation
      if (!manager.isFormValid()) {
        dispatch(updateStageAction(EDITING));
        dispatch(updateErrorAction('Form fields need attention'));
        return;
      }
    }

    if (stage === VALIDATED) {
      dispatch(updateStageAction(SUBMITTING));
    }
  }, [dispatch, manager, stage]);

  const handleNavigation = useCallback(() => {
    stage && dispatch(handleBackNavAction(stage));
  }, [dispatch, stage]);

  const handleDelete = useCallback(() => {
    rule?.id &&
      rootDispatch(
        deleteTransactionRuleDialog({
          ruleId: rule.id,
          source: 'ruleEdit',
          onDelete: () => dispatch(updateStageAction(CLEARED)),
        }),
      );
  }, [dispatch, rootDispatch, rule]);

  const handleCancel = useCallback(() => {
    // show confirmation dialog on create but not edit
    rule && rule.id
      ? dispatch(updateStageAction(CLEARED))
      : rootDispatch(cancelTransactionRuleDialog({ onCancel: () => dispatch(updateStageAction(CLEARED)) }));
  }, [dispatch, rootDispatch, rule]);

  useEffect(() => {
    if (!stage) {
      rule ? dispatch(updateStageAction(EDITING)) : onUpdate(undefined);
    } else if (stage === 'EDITING' && !rule) {
      onUpdate(undefined);
    } else if (stage === 'SUBMITTING' && rule) {
      // if we have a duplicateId in the state, set the rule id so we treat this as an update
      const newRule = state.duplicateId ? { ...rule, id: state.duplicateId } : rule;
      onUpdate(newRule);
    } else if (stage === CLEARED) {
      onUpdate(undefined);
    }
  }, [dispatch, onUpdate, rule, stage, state]);

  const showInstructions = stage !== VALIDATED && rule && !rule.id;

  return (
    <StdDialog
      dialogId={dialogId}
      title={titleText}
      size='md'
      showCloseButton
      onClose={() => {
        handleCancel();
      }}
      paperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <StdDialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          backgroundColor: 'background.default',
          px: 5,
          py: 2,
        }}
      >
        <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ pb: 1.75 }}>
          <Button
            onClick={handleNavigation}
            id='settings-drawer-backnav'
            startIcon={<ArrowBackIcon />}
            sx={{ textTransform: 'none' }}
          >
            <Typography variant='bodyRegular' sx={{ fontWeight: 500 }}>
              {backText}
            </Typography>
          </Button>
          {stage === VALIDATED ? (
            <Box sx={{ width: 140, minHeight: 45 }} />
          ) : showInstructions ? (
            <Box
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                background: theme.palette.background.paper,
                borderRadius: theme.spacing(1.5),
                padding: `${theme.spacing(1.5)} ${theme.spacing(2)}`,
                boxShadow: theme.shadows[10],
              })}
            >
              <RuleInstructions />
            </Box>
          ) : (
            <Box sx={{ width: 140 }} />
          )}
          <Box sx={{ width: 140 }} />
        </Stack>
        {(stage === EDITING || stage === RESOLVING) && <RuleEditorPanels />}
        {stage === VALIDATED && rule && <RuleReview />}
      </StdDialogContent>
      <StdDialogActions
        divider
        primary={{ type: 'button' }}
        tertiaryNode={
          <Box sx={{ width: '100%' }}>
            <RuleFooter onSubmit={handleSubmit} onCancel={handleCancel} onDelete={handleDelete} />
          </Box>
        }
      />
    </StdDialog>
  );
};

const RuleFormWrapper = ({ children }: { children: React.ReactNode }): JSX.Element => {
  const { state, dispatch } = useStateContext();
  const store = useStore();

  const onSubmit: SubmitHandler<SubmitPayload, boolean> = useCallback(
    async ({ payload }: { payload: SubmitPayload }) => {
      const { transactionRule } = payload;

      if (transactionRule) {
        // update the rule in the context based on the form submit values
        dispatch(updateRuleAction(transactionRule));

        // update or clear error
        const error = checkRuleConditions(payload);
        dispatch(updateErrorAction(error));
        if (error) {
          return;
        }

        // update or clear duplicateId
        const duplicateId = getDuplicateRuleId(store.getState(), transactionRule);
        dispatch(updateDuplicateIdAction(duplicateId));
        if (duplicateId) {
          return;
        }

        // update stage based on errors & duplicate checks
        dispatch(updateStageAction(VALIDATED));
      }
    },
    [dispatch, store],
  );

  return (
    <FormProvider input={state.rule || clearState} onSubmit={onSubmit}>
      {children}
    </FormProvider>
  );
};

const LlmRuleStdDialogComponent = ({
  dialogId,
  transaction,
  rule,
  handleClose,
  originalIsReviewed,
}: LlmRuleDialogProps) => {
  const appDispatch = useAppDispatch();

  const txnRuleV2 = Boolean(useAppSelector((state) => getFeatureFlag(state, 'qbpUsageTxnrulesv2'))); // ensure re-renders on state changes
  const businessCoaIds = useAppSelector((state) => categoriesSelectors.getBusinessCoaIds(state))?.filter(
    (id): id is string => Boolean(id),
  );

  const handleOnUpdate = useCallback(
    (newRule?: TransactionRuleType) => {
      if (newRule) {
        submitTransactionRule({ dispatch: appDispatch, rule: newRule });
      }
      handleClose?.(newRule);
    },
    [appDispatch, handleClose],
  );

  const { statementPayeeName, inferredPayeeName } = getNormalizedPayeeNames(transaction);

  const enabledFlags = getEnabledOverrides({ transaction, originalIsReviewed });

  // When creating a rule from a transaction, pre-fill the rule with the transaction's data
  // When editing an existing rule, use that rule's data
  // Otherwise, start with a clear state
  const getRule = useCallback((): TransactionRuleType => {
    return getInitialRule({
      transaction,
      rule,
      businessCoaIds,
      statementPayeeName,
      txnRuleV2,
    });
  }, [transaction, rule, businessCoaIds, statementPayeeName, txnRuleV2]);

  return (
    <RuleProvider
      rule={getRule()}
      onUpdate={handleOnUpdate}
      statementPayeeName={statementPayeeName ?? undefined}
      inferredPayeeName={inferredPayeeName ?? undefined}
      fromTxn={!!transaction}
      enabledOverrides={transaction ? enabledFlags : {}}
    >
      <RuleFormWrapper>
        <RuleDialogContent dialogId={dialogId} />
      </RuleFormWrapper>
    </RuleProvider>
  );
};

const LlmRuleStdDialog = React.memo(LlmRuleStdDialogComponent);

LlmRuleStdDialog.displayName = 'LlmRuleStdDialog';

export default LlmRuleStdDialog;
